import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  Alert,
} from "@mui/material";
import { Close, Refresh } from "@mui/icons-material";
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import moment from "moment";
import { hostname } from "../../../utils";

interface ActionMetricsDialogProps {
  open: boolean;
  onClose: () => void;
  listId: string;
  actionId: string;
  actionType: string;
  actionName: string;
  companyId: string;
  getToken: () => Promise<string>;
}

interface QueueStatus {
  queued: number;
  oldestQueuedDate?: string | null;
}

const ActionMetricsDialog: React.FC<ActionMetricsDialogProps> = ({
  open,
  onClose,
  listId,
  actionId,
  actionType,
  actionName,
  companyId,
  getToken,
}) => {
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState<QueueStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchMetrics = async (isInitial: boolean = false) => {
    if (isInitial) {
      setLoading(true);
    }
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/lists/${listId}/action/${actionId}/queue-status`,
        {
          params: {
            companyId,
            actionType,
            includeDetails: "true",
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setMetrics(response.data);

      // If queue is empty, stop polling
      if (response.data.queued === 0 && pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load metrics");
    } finally {
      if (isInitial) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (open) {
      fetchMetrics(true);

      // Poll every 2 seconds while dialog is open
      pollIntervalRef.current = setInterval(() => {
        fetchMetrics(false);
      }, 2000);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [open]);

  const isComplete = metrics?.queued === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{actionName}</Typography>
          <Box>
            <IconButton
              onClick={() => fetchMetrics(false)}
              disabled={loading}
              size="small"
              sx={{ mr: 1 }}
            >
              <Refresh />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && !metrics && (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <LinearProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {metrics && (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            {isComplete ? (
              <Typography variant="h6" color="primary">
                Complete
              </Typography>
            ) : (
              <>
                <Typography variant="h6">
                  {metrics.queued} items remaining
                </Typography>
                {metrics.oldestQueuedDate && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Started {moment.utc(metrics.oldestQueuedDate).local().fromNow()}
                  </Typography>
                )}
              </>
            )}
          </Paper>
        )}

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ActionMetricsDialog;
